import { Button, DatePicker, Empty, Input, Table, Card, Space, Popover } from "antd"
import * as React from "react";
import moment from "moment";
import 'moment-timezone';
import { Loader } from "rsuite";
import { InfoCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { AppContext } from '../../context/AppContext';
import { FileText, Download } from 'react-feather';
import dayjs from "dayjs";
import { useCallback, useEffect } from "react";
import { PartnersUploadAwardsFile } from "./modals/PartnersUploadAwardsFileModal";
import { PartnersDownloadReports } from "./modals/PartnersDownloadReportsModal";
import { SortOrder } from "antd/es/table/interface";
import { AlignType } from 'rc-table/lib/interface'

const pointsGiftLogo = require('../../assets/images/points-logo.png')
const pointsBalanceLogo = require('../../assets/images/points-balance-logo.png')
const starIcon = require('../../assets/icons/blue-star-icon.png')

export const PartnersTransactions = () => {
    const context = React.useContext(AppContext);
    const { onGetPartnersTransactions, onGetPartnersPointsValues } = context;
    const [loading, setLoading] = React.useState(false)
    const [transactions, setTransactions] = React.useState([])
    const [searchText, setSearchText] = React.useState("")
    const [fromDate, setFromDate] = React.useState<any>(null)
    const [toDate, setToDate] = React.useState<any>(null)
    const [awardModalVisible, setAwardModalVisible] = React.useState(false);
    const [reportModalVisible, setReportModalVisible] = React.useState(false);
    const [totalPoints, setTotalPoints] = React.useState(null);
    const [pointsBalance, setPointsBalance] = React.useState(null);
    
    const columns = [
        {
            title: 'Date & Time',
            dataIndex: 'dateTime',
            key: 'dateTime',
            width: '17%',
            sortDirections: ['ascend', 'descend'] as SortOrder[],
            defaultSortOrder: 'ascend' as SortOrder,
            sorter: (a: any, b: any) => moment(a.dateTime).unix() - moment(b.dateTime).unix(),
            render: (date: any) => {
                return (
                    <div>
                        <span>
                            {moment(date).format('ddd MMM D YYYY')}
                        </span>
                        <br />
                        <span className="text-neutralBlack-10">
                            {moment(date).tz("Asia/Taipei").format("HH:mm:ss [GMT]Z")}
                        </span>
                    </div>
                )
            },
        },
        {
            title: 'User',
            dataIndex: 'user',
            key: 'user',
            width: '15%',
        },
        {
            title: 'Channel',
            dataIndex: 'channel',
            key: 'channel',
        },
        {
            title: 'Transaction type',
            dataIndex: 'transactionType',
            key: 'transactionType',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (text: any, record: any) => {
                const statusColors = getStatusColor(text);
                return (
                    <div className="text-center py-1 px-4 w-fit rounded-lg" style={{ backgroundColor: statusColors.background, color: statusColors.color }}>
                        {text}
                    </div>
                )
            },
        },
        {
            title: 'Details',
            dataIndex: 'details',
            key: 'details',
            width: '25%'
        },
        {
            title: 'Points',
            dataIndex: 'points',
            key: 'points',
            width: '10%',
            align: 'right' as AlignType,
            render: (text: any, record: any) => {
                return (
                    <div className="pr-5">
                        { Number(text).toLocaleString() }
                    </div>
                )
            },
            onHeaderCell: () => ({
                className: 'points-table-header'
            }),        
        }
    ]

    const mapStatus = (status: string) => {
        switch (status) {
            case 'PENDING':
                return 'Pending';
            case 'TRANSFERRED':
                return 'Successful';
            case 'DECLINED':
                return 'Failed';
            default:
                return '';
        }
    };

    const mapTransactionType = (type: string) => {
        switch (type) {
            case 'EARN':
                return 'Earned';
            case 'REDEEM':
                return 'Redeemed';
            case 'CREDIT':
                return 'Credited';
            case 'DEBIT':
                return 'Debited';
            default:
                return '';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Failed':
                return { color: '#DB3030', background: '#FEEEEE' };
            case 'Processing':
                return { color: '#EA840D', background: '#FEF5E7' };
            case 'Successful':
                return { color: '#3AA829', background: '#E7F5E4' };
            default:
                return { color: 'transparent', background: 'transparent' };
        }
    };

    const getPointCardValues = useCallback(async () => {
        try {
            const response = await onGetPartnersPointsValues();
            
            if(response) {
                setTotalPoints(response.points.ern_pts)
                setPointsBalance(response.points.balance)
            }
        } catch (e) {
            setTotalPoints(null)
            setPointsBalance(null)
            console.error(e);
        } finally {
            return;
        }
    }, [onGetPartnersPointsValues]);;


    const getTransactions = useCallback(async () => {
        try {
            setLoading(true);
            const response = await onGetPartnersTransactions();

            if(response) {
                const data = response.items;

                const partnersTransactions = data.map((item: any, index: any) => ({
                    key: `${item.reference_id}-${index}`,
                    dateTime: item.timestamp,
                    user: `${item.first_name} ${item.last_name}`,
                    channel: 'API', // hardcoded for now (https://app.clickup.com/t/86endzxtw?comment=90180008563826&threadedComment=90180009034961)
                    transactionType: mapTransactionType(item.type),
                    status: mapStatus(item.status),
                    details: item?.details !== "" ? item.details : 'N/A',
                    points: item.points, 
                }));

                setTransactions(partnersTransactions);
            } else {
                setTransactions([]);
            }
        } catch (e) {
            setTransactions([]);
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [onGetPartnersTransactions, setTransactions, setLoading]);

    const handleSearch = () => {
        getTransactions();
    };

    const filterTransactions = (transaction: any) => {
        const lowerCaseSearchText = searchText.toLowerCase();
        const transactionDate = dayjs(transaction.dateTime);

        const isWithinDateRange =
            (!fromDate || transactionDate.isSame(fromDate, "day") || transactionDate.isAfter(fromDate, "day")) &&
            (!toDate || transactionDate.isSame(toDate, "day") || transactionDate.isBefore(toDate, "day"));

        const matchesSearchText = transaction.user.toLowerCase().includes(lowerCaseSearchText);

        return isWithinDateRange && matchesSearchText;
    };

    const locale = {
        emptyText: <Empty />,
    };

    const pointsToltipContent = (
        <div className="p-2 popver-div">
            <div className="text-neutralGray font-bold text-header5 flex items-left">
                <img className="mr-2 mt-1" style={{ height: '18px' }} src={starIcon} alt="Points Balance Start Icon" />
                Points balance
            </div>
            <p className="text-neutralGray-75 mt-2">
                This is the total Suki
                <br />
                Advantage points remaining 
                <br />
                from all your purchases.
            </p>
        </div>
    );

    const handleOpenAwardModal = () => {
        setAwardModalVisible(true);
    };

    const handleAwardModalOk = () => {
        setAwardModalVisible(false);
    };

    const handleAwardModalCancel = () => {
        setAwardModalVisible(false);
    };

    const handleOpenReportModal = () => {
        setReportModalVisible(true);
    };

    const handleReportModalOk = () => {
        setReportModalVisible(false);
    };

    const handleReportModalCancel = () => {
        setReportModalVisible(false);
    };

    const fromDatePickerDisabledDate = (day: any) => {
        if (toDate) {
          return day > moment().endOf('day') || day > toDate.endOf('day');
        } else {
          return day && day > moment().endOf('day');
        }
      };
    
    const toDatePickerDisabledDate = (day: any) => {
        return day > moment().endOf('day') || day < fromDate.startOf('day');
    };

    React.useEffect(() => {
        getTransactions()
    }, [searchText, fromDate, toDate, getTransactions]);

    useEffect(() => {
        getPointCardValues()
    }, [getPointCardValues]);
    
    return (
        <div>
            {loading && <Loader size="lg" backdrop />}
            <div className="flex flex-row justify-between items-center">
            <div className="text-header1 pt-16 font-poppinsSemiBold text-neutralBlack">Transactions</div>
                <div className="flex justify-between">
                    <Card className="mr-4 p-0" style={{ width: '350px', height: '116px', display: 'flex', alignItems: 'center' }}>
                        <Space>
                            <img className="mr-2 -mt-6" style={{ height: '50px' }} src={pointsGiftLogo} alt="Points Gift Logo" />
                            <div>
                                <div className="balances-card">Total points awarded this week</div>
                                <div className="text-neutralBlack text-header1 font-poppinsSemiBold">{totalPoints?.toLocaleString()}</div>
                            </div>
                        </Space>
                    </Card>
                    <Card className="p-0" style={{ width: '250px', height: '116px', display: 'flex', alignItems: 'center' }}>
                        <Space>
                            <img className="mr-2 -mt-6" style={{ height: '50px' }} src={pointsBalanceLogo} alt="Points Balance Logo" />
                            <div>
                                <div className="balances-card">
                                    Points balance
                                    <Popover placement="bottomLeft" content={pointsToltipContent}>
                                        <InfoCircleOutlined className="info-circle-icon cursor-pointer" style={{ verticalAlign: 'middle'}} />
                                    </Popover>
                                </div>
                                <div className="text-neutralBlack text-header1 font-poppinsSemiBold">{pointsBalance?.toLocaleString()}</div>
                            </div>
                        </Space>
                    </Card>
                </div>
            </div>
            <div className="mt-10">
                <div className="py-6 px-4 bg-neutralWhite-pure flex items-center">
                    <Input
                        placeholder="Search"
                        allowClear
                        onChange={(e) => setSearchText(e.target.value)}
                        onPressEnter={handleSearch}
                        style={{ width: 250, height: 40, marginRight: 16 }}
                        prefix={<SearchOutlined style={{ marginRight: 8 }} />}
                    />
                    <span className="pl-4 font-poppinsRegular">Filter by</span>
                    <DatePicker
                        style={{ width: 130, height: 40 }}
                        className="mx-3 font-poppinsRegular"
                        onChange={(date) => setFromDate(date)}
                        value={fromDate}
                        format="MM/DD/YYYY"
                        disabledDate={fromDatePickerDisabledDate}
                    />
                    <span className="font-poppinsRegular">to</span>
                    <DatePicker
                        style={{ width: 130, height: 40, }}
                        className="mx-3 font-poppinsRegular"
                        onChange={(date) => setToDate(date)}
                        value={toDate}
                        format="MM/DD/YYYY"
                        disabled={fromDate ? false : true}
                        disabledDate={toDatePickerDisabledDate}
                    />

                    <div className="flex ml-auto">
                        <Button className="bg-primary font-poppinsRegular text-xs leading-6 border-primary hover:bg-primary-hovered hover:text-neutral-50 active:border-primary-border active:bg-primary-clicked" style={{ alignItems: 'center', display: 'flex', height: 40, color: 'white', marginRight: '24px' }} onClick={() => {
                            handleOpenAwardModal();
                        }}>
                            <FileText size={20} className="h-4 mr-1 -ml-1"/>
                            Upload earn file
                        </Button>
                        <Button className="bg-primary font-poppinsRegular text-xs leading-6 border-primary hover:bg-primary-hovered hover:text-neutral-50 active:border-primary-border active:bg-primary-clicked" style={{ alignItems: 'center', display: 'flex', height: 40, color: 'white' }} onClick={() => {
                            handleOpenReportModal();
                        }}>
                            <Download size={20} className="h-4 mr-1 -ml-1"/>
                            Download Reports
                        </Button>
                        <PartnersUploadAwardsFile
                            onOkCallback={handleAwardModalOk}
                            open={awardModalVisible}
                            onClose={handleAwardModalCancel}
                        />
                        <PartnersDownloadReports
                            onOkCallback={handleReportModalOk}
                            open={reportModalVisible}
                            onClose={handleReportModalCancel}
                        />
                    </div>
                </div>
                <Table
                    columns={columns}
                    dataSource={transactions.filter(filterTransactions)}
                    locale={locale}
                    scroll={{ y: 700 }}
                    pagination={{
                        position: ['bottomRight'],
                        showSizeChanger: false
                    }}
                >
                </Table>
            </div>
        </div>
    )
}